import React, { useEffect, useRef, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, Copy, Image, Info, Trash2 } from '../bravosIcons';
import {
  DragHandleBlock,
  DragHandleExercise,
  FieldEditIcon,
  IconMinusRow,
  IconPlus,
  NoteEditIcon,
} from './TrainerWorkoutDetailEditIcons';
import type { WorkoutWithBlocks } from '../../../types/dataModel';
import { getWorkoutDisplayBannerUrl, isAllowlistedWorkoutBannerUrl } from '../../../lib/workoutBannerImage';
import { getYouTubeThumbnail } from '../../../utils/youtubeUtils';
import { useTrainerWorkoutEditState } from './hooks/useTrainerWorkoutEditState';
import { useTrainerWorkoutAddHighlights } from './hooks/useTrainerWorkoutAddHighlights';
import {
  computeExerciseRuns,
  CREATE_REQUIRED_FIELD_LABELS,
  deriveWorkoutEquipmentAndMuscles,
  getCreateMissingRequiredFields,
  isCreateReady,
  blurEditableOnEnter,
  formatBlockTypeLabel,
  isRegularBlock,
  isV1BlockType,
  V1_BLOCK_TYPE_OPTIONS,
} from './utils/trainerWorkoutEditUtils';
import { HeroBannerPlaceholderSvg, VideoTilePlaceholderSvg } from './TrainerWorkoutEditMockArt';
import { TrainerConfirmDialog } from './overlays/TrainerConfirmDialog';
import { TrainerWorkoutBannerGallery } from './overlays/TrainerWorkoutBannerGallery';
import { TrainerWorkoutAddBlockModal } from './overlays/TrainerWorkoutAddBlockModal';
import { TrainerWorkoutAddExerciseDrawer } from './overlays/TrainerWorkoutAddExerciseDrawer';

const META_SEP = '\u00B7';

type EditConfirmState =
  | { kind: 'block'; blockIndex: number; blockName: string }
  | { kind: 'exercise'; blockIndex: number; groupKey: string; exerciseName: string }
  | { kind: 'discard' };

export type TrainerWorkoutDetailEditViewProps = {
  workout: WorkoutWithBlocks;
  mode?: 'edit' | 'create';
  onSaved: (result: WorkoutWithBlocks) => void | Promise<void>;
  onCreated?: (result: WorkoutWithBlocks) => void | Promise<void>;
  onExitEditMode: () => void;
  onDiscardNavigate?: () => void;
  /** Opens workout delete confirmation (parent shows library-style dialog). */
  onRequestDeleteWorkout?: () => void;
  onDuplicateWorkout?: () => void;
  duplicateInFlight?: boolean;
  checkWorkoutNameExists?: (name: string, excludeWorkoutId?: string) => boolean;
  favoriteExerciseIds?: string[];
  onDirtyChange?: (dirty: boolean) => void;
};

function SortableBlockShell({
  id,
  children,
}: {
  id: string;
  children: (props: { dragHandleProps: React.HTMLAttributes<HTMLButtonElement> }) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({
        dragHandleProps: { ...attributes, ...listeners },
      })}
    </div>
  );
}

function SortableRunShell({
  id,
  children,
}: {
  id: string;
  children: (props: { dragHandleProps: React.HTMLAttributes<HTMLButtonElement> }) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({
        dragHandleProps: { ...attributes, ...listeners },
      })}
    </div>
  );
}

export function TrainerWorkoutDetailEditView({
  workout,
  mode = 'edit',
  onSaved,
  onCreated,
  onExitEditMode,
  onDiscardNavigate,
  onRequestDeleteWorkout,
  onDuplicateWorkout,
  duplicateInFlight,
  checkWorkoutNameExists,
  favoriteExerciseIds,
  onDirtyChange,
}: TrainerWorkoutDetailEditViewProps) {
  const isCreate = mode === 'create';
  const edit = useTrainerWorkoutEditState({
    workout,
    mode,
    onSaved,
    onCreated,
    checkWorkoutNameExists,
  });
  const addHighlights = useTrainerWorkoutAddHighlights(
    edit.blocks,
    edit.editedWorkout != null,
    isCreate ? 'create' : workout.id,
  );
  const [bannerOpen, setBannerOpen] = useState(false);
  const [addBlockOpen, setAddBlockOpen] = useState(false);
  const [addExerciseBlockIndex, setAddExerciseBlockIndex] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<EditConfirmState | null>(null);
  /** Block expand/collapse — first block open by default (matches view workout). */
  const [openBlocks, setOpenBlocks] = useState<Set<string>>(new Set());
  const prevBlockIdsRef = useRef<Set<string>>(new Set());
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    onDirtyChange?.(edit.isDirty);
  }, [edit.isDirty, onDirtyChange]);

  useEffect(() => {
    if (!edit.nameError) return;
    const el = nameInputRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      const main = el.closest('.twd-main') as HTMLElement | null;
      if (main) {
        const mainRect = main.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const delta =
          elRect.top - mainRect.top - main.clientHeight / 2 + elRect.height / 2;
        main.scrollBy({ top: delta, behavior: 'smooth' });
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      el.focus({ preventScroll: true });
    });
  }, [edit.nameError, edit.nameErrorSeq]);

  useEffect(() => {
    const ids = edit.blocks.map((b) => b.id).filter((id): id is string => Boolean(id));
    const current = new Set(ids);
    const prev = prevBlockIdsRef.current;
    const added = ids.filter((id) => !prev.has(id));
    prevBlockIdsRef.current = current;

    setOpenBlocks((prevOpen) => {
      const next = new Set(Array.from(prevOpen).filter((id) => current.has(id)));
      if (prev.size === 0 && ids.length > 0) {
        return new Set([ids[0]]);
      }
      for (const id of added) {
        next.add(id);
      }
      return next;
    });
  }, [edit.blocks]);

  const toggleBlockOpen = (blockId: string) => {
    setOpenBlocks((prev) => {
      const next = new Set(Array.from(prev));
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const w = edit.editedWorkout;
  if (!w) return null;

  const { equipment, muscles } = deriveWorkoutEquipmentAndMuscles(w);
  const bannerTrim = (w.bannerImageUrl || '').trim();
  const heroBg = isCreate
    ? isAllowlistedWorkoutBannerUrl(bannerTrim)
      ? bannerTrim
      : ''
    : getWorkoutDisplayBannerUrl({ id: w.id, bannerImageUrl: w.bannerImageUrl });
  const blockIds = edit.blocks.map((b, i) => b.id || `block-${i}`);
  const exerciseCount = edit.blocks.reduce((acc, b) => {
    const runs = computeExerciseRuns(b.exerciseSets ?? []);
    return acc + runs.filter((r) => r.exercise?.id).length;
  }, 0);

  const handleBlockDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = blockIds.indexOf(String(active.id));
    const newIndex = blockIds.indexOf(String(over.id));
    if (oldIndex >= 0 && newIndex >= 0) edit.reorderBlocks(oldIndex, newIndex);
  };

  const handleRunDragEnd = (blockIndex: number, runIds: string[], event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = runIds.indexOf(String(active.id));
    const newIndex = runIds.indexOf(String(over.id));
    if (oldIndex >= 0 && newIndex >= 0) edit.reorderExerciseRun(blockIndex, oldIndex, newIndex);
  };

  const handleDiscard = () => {
    if (edit.isDirty) {
      setConfirm({ kind: 'discard' });
      return;
    }
    if (isCreate) {
      onDiscardNavigate?.();
      return;
    }
    edit.discard();
    onExitEditMode();
  };

  const runDiscard = () => {
    edit.discard();
    setConfirm(null);
    if (isCreate) {
      onDiscardNavigate?.();
      return;
    }
    onExitEditMode();
  };

  const handleSave = async () => {
    await edit.save();
  };

  const blockCount = edit.blocks.length;
  const missingRequired = isCreate ? getCreateMissingRequiredFields(w, bannerTrim) : [];
  const createReady = isCreate ? isCreateReady(w, bannerTrim) : false;

  const createBarBadge = createReady
    ? { className: 'create-badge-ready', text: 'Ready to create' }
    : {
        className: 'create-badge-warn',
        text:
          missingRequired.length === 1
            ? `${CREATE_REQUIRED_FIELD_LABELS[missingRequired[0]]} required`
            : `${missingRequired.length} fields required`,
      };

  const createBarHelper = createReady
    ? blockCount === 0
      ? 'You can add blocks after creating'
      : `${blockCount} block${blockCount === 1 ? '' : 's'} added`
    : `Required: ${missingRequired.map((f) => CREATE_REQUIRED_FIELD_LABELS[f]).join(', ')}`;

  const titleMetaFields = (
    <>
      <div className="title-card-field">
        <label className="title-card-label" htmlFor="twe-create-level">
          Level <span className="title-card-required">*</span>
        </label>
        <select
          id="twe-create-level"
          className="title-card-select"
          value={w.difficulty ?? ''}
          onChange={(e) =>
            edit.updateField('difficulty', (e.target.value || undefined) as WorkoutWithBlocks['difficulty'])
          }
        >
          <option value="" disabled>
            Select level
          </option>
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
      </div>
      <div className="title-card-field">
        <label className="title-card-label" htmlFor="twe-create-duration">
          Duration <span className="title-card-required">*</span>
        </label>
        <div className="title-card-duration">
          <input
            id="twe-create-duration"
            type="number"
            className="title-card-duration-input tnum"
            min={1}
            max={300}
            required
            placeholder="min"
            value={w.estimatedDurationMinutes ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              edit.updateField(
                'estimatedDurationMinutes',
                v === '' ? undefined : Math.max(1, Math.min(300, parseInt(v, 10) || 1)),
              );
            }}
          />
          <span className="title-card-duration-suffix">min</span>
        </div>
      </div>
    </>
  );

  return (
    <div className={`twe${isCreate ? ' twe--create' : ''}`} onKeyDown={blurEditableOnEnter}>
      {isCreate ? (
        <div className="create-banner">
          <div className="create-banner-left">
            <span className="edit-dot" aria-hidden="true" />
            <span className="create-banner-text">
              <strong>New workout.</strong> Fill in the details, then build your blocks.
            </span>
          </div>
        </div>
      ) : (
        <div className="edit-banner">
          <div className="edit-banner-left">
            <span className="edit-dot" aria-hidden="true" />
            <span className="edit-banner-text">
              <strong>Editing workout.</strong>
            </span>
          </div>
          <div className="edit-banner-actions">
            <button type="button" className="btn btn-danger" onClick={onRequestDeleteWorkout}>
              <Trash2 size={14} strokeWidth={2} />
              Delete workout
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={duplicateInFlight}
              onClick={onDuplicateWorkout}
            >
              <Copy size={14} strokeWidth={2} />
              Duplicate
            </button>
          </div>
        </div>
      )}

      {isCreate ? (
        <section className="title-card" aria-label="Workout details">
          <label className="title-card-label title-card-label--name" htmlFor="twe-create-name">
            Workout name <span className="title-card-required">*</span>
          </label>
          <div
            className={`title-card-name-wrap${edit.nameError ? ' title-card-name-wrap--error' : ''}`}
          >
            <input
              id="twe-create-name"
              ref={nameInputRef}
              type="text"
              className="title-card-name"
              value={w.name}
              placeholder="Name this workout…"
              onChange={(e) => edit.updateField('name', e.target.value)}
              aria-label="Workout title"
              aria-invalid={Boolean(edit.nameError)}
              aria-describedby={edit.nameError ? 'twe-name-error' : undefined}
              required
            />
          </div>
          {edit.nameError ? (
            <p id="twe-name-error" className="title-card-name-error" role="alert">
              {edit.nameError}
            </p>
          ) : null}
          <div className="title-card-meta">{titleMetaFields}</div>
        </section>
      ) : null}

      <section className="hero" aria-label="Workout hero edit">
        <div
          className={`hero-img${isCreate && !heroBg ? ' hero-img-empty' : ''}${isCreate && heroBg ? ' hero-img-create-filled' : ''}`}
          style={
            heroBg
              ? {
                  backgroundImage: `url(${heroBg})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : undefined
          }
          onClick={isCreate && !heroBg ? () => setBannerOpen(true) : undefined}
          onKeyDown={
            isCreate && !heroBg
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setBannerOpen(true);
                  }
                }
              : undefined
          }
          role={isCreate && !heroBg ? 'button' : undefined}
          tabIndex={isCreate && !heroBg ? 0 : undefined}
          aria-label={isCreate && !heroBg ? 'Choose cover image from gallery' : undefined}
        >
          {!heroBg ? (
            isCreate ? (
              <div className="hero-upload-zone">
                <Image size={24} aria-hidden />
                <span>Choose cover image</span>
                <span className="hero-upload-required">Required</span>
              </div>
            ) : (
              <HeroBannerPlaceholderSvg />
            )
          ) : null}
          {isCreate && heroBg ? (
            <div className="hero-img-overlay hero-img-overlay--cover-only">
              <button type="button" className="change-image-btn" onClick={() => setBannerOpen(true)}>
                <Image size={14} aria-hidden />
                Change image
              </button>
            </div>
          ) : null}
          {!isCreate && (
          <div className="hero-img-overlay">
            {heroBg ? (
              <button type="button" className="change-image-btn" onClick={() => setBannerOpen(true)}>
                <Image size={14} aria-hidden />
                Change image
              </button>
            ) : null}
            <div className="hero-title-edit">
              <div
                className={`workout-name-input-wrap${edit.nameError ? ' workout-name-input-wrap--error' : ''}`}
              >
                <input
                  id="twe-edit-name"
                  ref={nameInputRef}
                  type="text"
                  className="workout-name-input"
                  value={w.name}
                  onChange={(e) => edit.updateField('name', e.target.value)}
                  aria-label="Workout name"
                  aria-invalid={Boolean(edit.nameError)}
                  aria-describedby={edit.nameError ? 'twe-edit-name-error' : undefined}
                />
              </div>
              {edit.nameError ? (
                <p id="twe-edit-name-error" className="workout-name-input-error" role="alert">
                  {edit.nameError}
                </p>
              ) : null}
              <div className="hero-meta-edit tnum">
                <span className="meta-pill">
                  <span className="meta-label">Duration</span>
                  <input
                    type="number"
                    min={1}
                    max={300}
                    value={w.estimatedDurationMinutes ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      edit.updateField(
                        'estimatedDurationMinutes',
                        v === '' ? undefined : Math.max(1, Math.min(300, parseInt(v, 10) || 1)),
                      );
                    }}
                  />
                  <span className="meta-label">min</span>
                </span>
                <span className="meta-pill">
                  <span className="meta-label">Level</span>
                  <select
                    value={w.difficulty ?? ''}
                    onChange={(e) =>
                      edit.updateField(
                        'difficulty',
                        (e.target.value || undefined) as WorkoutWithBlocks['difficulty'],
                      )
                    }
                  >
                    <option value="">—</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </span>
              </div>
            </div>
          </div>
          )}
        </div>
        <div className="hero-info">
          <div className="info-block">
            <div className="field-label">
              Trainer note <span className="dash" />
            </div>
            <div className="field-editable-wrap">
              <textarea
                className="field-inline"
                rows={3}
                value={w.note ?? ''}
                placeholder="Add a note for this workout…"
                onChange={(e) => edit.updateField('note', e.target.value)}
              />
              <FieldEditIcon />
            </div>
          </div>
          <div className="info-block">
            <div className="field-label">
              Equipment <span className="dash" />
            </div>
            <div className="derived-section">
              <div className="chip-row">
                {equipment.length ? (
                  equipment.map((e) => (
                    <span key={e} className="chip">
                      {e}
                    </span>
                  ))
                ) : (
                  <span className="chip">—</span>
                )}
              </div>
              <div className="derived-notice">
                <Info width={12} height={12} aria-hidden />
                Reflects exercises in this workout
              </div>
            </div>
          </div>
          <div className="info-block">
            <div className="field-label">
              Muscle group <span className="dash" />
            </div>
            <div className="derived-section">
              <div className="chip-row">
                {muscles.length ? (
                  muscles.map((m) => (
                    <span key={m} className="chip muscle">
                      {m}
                    </span>
                  ))
                ) : (
                  <span className="chip muscle">—</span>
                )}
              </div>
              <div className="derived-notice">
                <Info width={12} height={12} aria-hidden />
                Reflects exercises in this workout
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="blocks-header">
        <h2>Exercise blocks</h2>
        <span className="block-count tnum">
          {edit.blocks.length} blocks {META_SEP} {exerciseCount} exercises
        </span>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleBlockDragEnd}>
        <SortableContext items={blockIds} strategy={verticalListSortingStrategy}>
          <>
            {edit.blocks.map((block, blockIndex) => {
              const blockId = block.id || `block-${blockIndex}`;
              const typeLower = String(block.type || 'Default').toLowerCase();
              const isAmrap = typeLower === 'amrap';
              const setsUi = edit.blockSets[blockId];
              const runs = computeExerciseRuns(block.exerciseSets ?? []);
              const runIds = runs.map((r) => `${blockId}-run-${r.groupKey}`);
              const typeLocked = edit.blockTypeLockedIds.has(blockId);
              const showV1Picker = isV1BlockType(block.type) || !block.type;
              const regularBlock = isRegularBlock(block.type);
              const blockDisplayName =
                block.name?.trim() || `Block ${blockIndex + 1}`;
              const blockOpen = openBlocks.has(blockId);

              return (
                <SortableBlockShell key={blockId} id={blockId}>
                  {({ dragHandleProps }) => (
                    <section
                      className={`block${blockOpen ? ' block-open' : ''}${addHighlights.isNewBlock(blockId) ? ' is-new' : ''}`}
                      data-highlight-block={blockId}
                    >
                      <div className="block-bar">
                        <span className="drag-handle" {...dragHandleProps}>
                          <DragHandleBlock />
                        </span>
                        <span className="block-num tnum">{String(blockIndex + 1).padStart(2, '0')}</span>
                        <div className="block-meta-edit">
                          <input
                            type="text"
                            className="block-name-input"
                            value={block.name || ''}
                            placeholder={`Block ${blockIndex + 1}`}
                            onChange={(e) => edit.updateBlock(blockIndex, { name: e.target.value })}
                          />
                          <div className="block-controls">
                            {typeLocked || !showV1Picker ? (
                              <span className="block-type-pill" aria-label="Block type">
                                {formatBlockTypeLabel(block.type)}
                              </span>
                            ) : (
                              <select
                                className="select-mini"
                                value={block.type || 'Default'}
                                onChange={(e) => edit.updateBlockType(blockIndex, e.target.value)}
                              >
                                {V1_BLOCK_TYPE_OPTIONS.map((o) => (
                                  <option key={o.db} value={o.db}>
                                    {o.ui}
                                  </option>
                                ))}
                              </select>
                            )}
                            {!regularBlock ? (
                              <select
                                className="select-mini accent tnum"
                                value={isAmrap ? String(setsUi === '' ? 5 : setsUi) : String(setsUi ?? block.sets ?? 1)}
                                onChange={(e) => edit.updateBlockSetsCount(blockIndex, e.target.value)}
                              >
                                {isAmrap
                                  ? [1, 3, 4, 5, 7, 10, 12, 15, 20].map((m) => (
                                      <option key={m} value={m}>
                                        {m} min
                                      </option>
                                    ))
                                  : Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                                      <option key={n} value={n}>
                                        {n} {n === 1 ? 'set' : 'sets'}
                                      </option>
                                    ))}
                              </select>
                            ) : null}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="block-toggle"
                          aria-expanded={blockOpen}
                          aria-label={blockOpen ? 'Collapse block' : 'Expand block'}
                          title={blockOpen ? 'Collapse block' : 'Expand block'}
                          onClick={() => toggleBlockOpen(blockId)}
                        >
                          <ChevronDown className="block-chevron" aria-hidden="true" />
                        </button>
                        <div className="block-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            title="Duplicate block"
                            onClick={() => edit.duplicateBlock(blockIndex)}
                          >
                            <Copy size={13} strokeWidth={2} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn danger"
                            title="Delete block"
                            onClick={() =>
                              setConfirm({
                                kind: 'block',
                                blockIndex,
                                blockName: blockDisplayName,
                              })
                            }
                          >
                            <Trash2 size={13} strokeWidth={2} />
                          </button>
                        </div>
                      </div>
                      {blockOpen ? (
                      <>
                      <div className="block-note-edit">
                        <div className="note-label">Block note</div>
                        <div className="note-input-wrap">
                          <textarea
                            className="note-input"
                            rows={2}
                            value={block.note ?? ''}
                            placeholder="Optional note for this block…"
                            onChange={(e) => edit.updateBlockNote(blockIndex, e.target.value)}
                          />
                          <NoteEditIcon />
                        </div>
                      </div>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(e) => handleRunDragEnd(blockIndex, runIds, e)}
                      >
                        <SortableContext items={runIds} strategy={verticalListSortingStrategy}>
                          {runs.map((run) => {
                            const runId = `${blockId}-run-${run.groupKey}`;
                            const first = run.sets[0];
                            const exName = first?.exercise?.name || 'Exercise';
                            const measureType = edit.getMeasureType(blockIndex, run.groupKey, first);
                            const repKey = `${blockIndex}-${run.groupKey}`;
                            const vary = edit.isRunVaryBySet(blockIndex, run.groupKey, run.sets);
                            const setCount = run.sets.length;
                            const thumb =
                              getYouTubeThumbnail(first?.exercise?.video) || first?.exercise?.image;
                            const rowsToShow = vary ? run.sets : [run.sets[0]];
                            const allSetsLabel =
                              setCount === 1 ? 'Set 01' : 'All sets';

                            return (
                              <SortableRunShell key={runId} id={runId}>
                                {({ dragHandleProps: runDrag }) => (
                                  <div
                                    className={`exercise-edit${addHighlights.isNewExercise(blockId, run.groupKey) ? ' is-new' : ''}`}
                                    data-highlight-exercise={`${blockId}:${run.groupKey}`}
                                  >
                                    <div className="ex-edit-main">
                                      <div className="ex-header-edit">
                                        <span className="drag-handle" style={{ marginLeft: -4 }} {...runDrag}>
                                          <DragHandleExercise />
                                        </span>
                                        <div className="ex-name-display" aria-label="Exercise name">
                                          {exName}
                                        </div>
                                        <button
                                          type="button"
                                          className="icon-btn danger"
                                          title="Remove exercise"
                                          onClick={() =>
                                            setConfirm({
                                              kind: 'exercise',
                                              blockIndex,
                                              groupKey: run.groupKey,
                                              exerciseName: exName,
                                            })
                                          }
                                        >
                                          <Trash2 size={13} strokeWidth={2} />
                                        </button>
                                      </div>
                                      <div className="ex-config-row">
                                        <span className="ex-config-label">Variation:</span>
                                        <select
                                          className="select-mini"
                                          value={vary ? 'vary' : 'same'}
                                          onChange={(e) =>
                                            edit.setRunVariation(
                                              blockIndex,
                                              run.groupKey,
                                              e.target.value === 'vary' ? 'vary' : 'same',
                                            )
                                          }
                                        >
                                          <option value="vary">Vary by set</option>
                                          <option value="same">Same across sets</option>
                                        </select>
                                      </div>
                                      <div>
                                        <p className="exercise-sets-meta tnum">
                                          <strong>
                                            {setCount} {setCount === 1 ? 'set' : 'sets'}
                                          </strong>
                                          <span className="exercise-meta-sep" aria-hidden>
                                            {META_SEP}
                                          </span>
                                          <span>{vary ? 'Vary by set' : 'Same set variation'}</span>
                                        </p>
                                        <div className="sets-table-header">
                                          <span className="sets-table-label">Sets</span>
                                          <div className="sets-metric-toggle" role="group">
                                            <button
                                              type="button"
                                              className={measureType === 'reps' ? 'active' : ''}
                                              onClick={() => {
                                                if (measureType !== 'reps')
                                                  edit.toggleMeasureType(blockIndex, run.groupKey);
                                              }}
                                            >
                                              Reps
                                            </button>
                                            <button
                                              type="button"
                                              className={measureType === 'duration' ? 'active' : ''}
                                              onClick={() => {
                                                if (measureType !== 'duration')
                                                  edit.toggleMeasureType(blockIndex, run.groupKey);
                                              }}
                                            >
                                              Duration
                                            </button>
                                          </div>
                                        </div>
                                        <div
                                          className={`sets-edit-table${measureType === 'duration' ? ' duration-mode' : ''}`}
                                        >
                                          <div className="sets-edit-row header">
                                            <span className="set-label">Set</span>
                                            <span className="col-label metric-col-label">
                                              {measureType === 'reps' ? 'Reps' : 'Duration'}
                                            </span>
                                            <span className="col-label">Weight</span>
                                            <span className="col-label">Rest</span>
                                            <span />
                                          </div>
                                          {rowsToShow.map((set, rowIdx) => {
                                            const setIndex = vary ? run.indices[rowIdx] : run.indices[0];
                                            const label = vary
                                              ? `Set ${String(rowIdx + 1).padStart(2, '0')}`
                                              : allSetsLabel;
                                            const syncIndices = vary
                                              ? [setIndex]
                                              : run.indices;
                                            return (
                                              <div
                                                key={vary ? set.id ?? setIndex : `all-${run.groupKey}`}
                                                className={`sets-edit-row${measureType === 'duration' ? ' duration-mode' : ''}${!vary ? ' all-sets-row' : ''}`}
                                              >
                                                <span className="set-label tnum">{label}</span>
                                                <div className="metric-cell">
                                                  {measureType === 'reps' ? (
                                                    <input
                                                      className="num-field reps-field"
                                                      type="text"
                                                      value={
                                                        (set as any).repText ??
                                                        (set.rep != null ? String(set.rep) : '')
                                                      }
                                                      placeholder="—"
                                                      onChange={(e) => {
                                                        const v = e.target.value;
                                                        syncIndices.forEach((idx) =>
                                                          edit.updateSetRepText(blockIndex, idx, v),
                                                        );
                                                      }}
                                                      onBlur={(e) => {
                                                        syncIndices.forEach((idx) =>
                                                          edit.updateSetRepText(
                                                            blockIndex,
                                                            idx,
                                                            e.target.value,
                                                            true,
                                                          ),
                                                        );
                                                      }}
                                                    />
                                                  ) : (
                                                    <input
                                                      className="duration-field"
                                                      type="text"
                                                      inputMode="numeric"
                                                      autoComplete="off"
                                                      value={edit.getTimeFieldDisplay(
                                                        'duration',
                                                        blockIndex,
                                                        setIndex,
                                                        set.durationSeconds,
                                                      )}
                                                      placeholder="0:00"
                                                      onChange={(e) =>
                                                        edit.setTimeFieldDraft(
                                                          'duration',
                                                          blockIndex,
                                                          setIndex,
                                                          e.target.value,
                                                        )
                                                      }
                                                      onBlur={() =>
                                                        edit.commitTimeFieldDraft(
                                                          'duration',
                                                          blockIndex,
                                                          syncIndices,
                                                        )
                                                      }
                                                    />
                                                  )}
                                                </div>
                                                <div className="num-field-wrap">
                                                  <input
                                                    className="num-field"
                                                    type="number"
                                                    min={0}
                                                    step="0.5"
                                                    value={(set as any).weightKg ?? ''}
                                                    placeholder="—"
                                                    onChange={(e) => {
                                                      syncIndices.forEach((idx) =>
                                                        edit.updateSetWeight(
                                                          blockIndex,
                                                          idx,
                                                          e.target.value,
                                                        ),
                                                      );
                                                    }}
                                                  />
                                                  <span className="num-suffix">kg</span>
                                                </div>
                                                <input
                                                  className="num-field"
                                                  type="text"
                                                  inputMode="numeric"
                                                  autoComplete="off"
                                                  placeholder="—:—"
                                                  value={edit.getTimeFieldDisplay(
                                                    'rest',
                                                    blockIndex,
                                                    setIndex,
                                                    set.restSeconds,
                                                  )}
                                                  onChange={(e) =>
                                                    edit.setTimeFieldDraft(
                                                      'rest',
                                                      blockIndex,
                                                      setIndex,
                                                      e.target.value,
                                                    )
                                                  }
                                                  onBlur={() =>
                                                    edit.commitTimeFieldDraft(
                                                      'rest',
                                                      blockIndex,
                                                      syncIndices,
                                                    )
                                                  }
                                                />
                                                {vary && run.sets.length > 1 ? (
                                                  <button
                                                    type="button"
                                                    className="icon-btn danger sets-remove-btn"
                                                    title="Remove set"
                                                    aria-label="Remove set"
                                                    onClick={() => edit.removeSet(blockIndex, setIndex)}
                                                  >
                                                    <Trash2 size={13} strokeWidth={2} />
                                                  </button>
                                                ) : (
                                                  <span />
                                                )}
                                              </div>
                                            );
                                          })}
                                          <div className="add-set-row">
                                            <button
                                              type="button"
                                              className="add-set-btn"
                                              onClick={() => edit.addSetToRun(blockIndex, run.groupKey)}
                                            >
                                              <IconPlus /> Add set
                                            </button>
                                            {!vary && run.sets.length > 1 ? (
                                              <button
                                                type="button"
                                                className="add-set-btn remove-set-btn"
                                                onClick={() => edit.removeSet(blockIndex, run.lastIndex)}
                                              >
                                                <IconMinusRow /> Remove set
                                              </button>
                                            ) : null}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="video-tile-edit">
                                      {thumb ? (
                                        <img
                                          src={thumb}
                                          alt=""
                                          style={{
                                            position: 'absolute',
                                            inset: 0,
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                          }}
                                        />
                                      ) : (
                                        <VideoTilePlaceholderSvg idSuffix={runId} />
                                      )}
                                    </div>
                                  </div>
                                )}
                              </SortableRunShell>
                            );
                          })}
                        </SortableContext>
                      </DndContext>
                      <div className="add-exercise-row">
                        <button
                          type="button"
                          className="add-exercise-btn"
                          onClick={() => setAddExerciseBlockIndex(blockIndex)}
                        >
                          <IconPlus size={16} /> Add exercise to this block
                        </button>
                      </div>
                      </>
                      ) : null}
                    </section>
                  )}
                </SortableBlockShell>
              );
            })}
          </>
        </SortableContext>
      </DndContext>

      <button type="button" className="add-block-btn" onClick={() => setAddBlockOpen(true)}>
        <span className="icon-wrap">
          <IconPlus size={16} />
        </span>
        Add new block
      </button>

      {isCreate ? (
        <div className="create-bar-dock">
          <div className="create-bar">
            <div className="create-bar-left">
              <span className={`create-bar-badge ${createBarBadge.className}`}>{createBarBadge.text}</span>
              <span className="create-bar-helper">{createBarHelper}</span>
            </div>
            <div className="create-bar-actions">
              <button type="button" className="btn btn-ghost" onClick={handleDiscard}>
                Discard
              </button>
              <button
                type="button"
                className="btn btn-create"
                disabled={!createReady || edit.isSaving}
                onClick={() => void handleSave()}
              >
                {edit.isSaving ? 'Creating…' : 'Create workout'}
              </button>
            </div>
          </div>
        </div>
      ) : edit.dirtyCount > 0 ? (
        <div className="save-bar-dock">
          <div className="save-bar">
            <div className="save-bar-status">There are unsaved changes.</div>
            <div className="save-bar-actions">
              <button type="button" className="btn btn-ghost" onClick={handleDiscard}>
                Discard
              </button>
              <button
                type="button"
                className="btn btn-save"
                disabled={edit.isSaving}
                onClick={() => void handleSave()}
              >
                {edit.isSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {edit.saveError ? (
        <div className="save-bar" style={{ marginTop: 12, background: 'rgba(255,91,58,0.12)', color: 'var(--warn)' }} role="alert">
          <div>
            <strong>{edit.saveError.title}</strong>
            <p style={{ margin: 0, fontSize: 13 }}>{edit.saveError.message}</p>
          </div>
        </div>
      ) : null}

      <TrainerWorkoutBannerGallery
        open={bannerOpen}
        selectedUrl={w.bannerImageUrl}
        onSelect={(url) => edit.updateField('bannerImageUrl', url)}
        onClose={() => setBannerOpen(false)}
      />
      <TrainerWorkoutAddBlockModal
        open={addBlockOpen}
        onClose={() => setAddBlockOpen(false)}
        onCreate={(name, structure) => edit.addBlockFromModal(name, structure)}
      />
      {addExerciseBlockIndex !== null && (
        <TrainerWorkoutAddExerciseDrawer
          open
          blockIndex={addExerciseBlockIndex}
          blockLabel={edit.blocks[addExerciseBlockIndex]?.name || `Block ${addExerciseBlockIndex + 1}`}
          favoriteExerciseIds={favoriteExerciseIds}
          onClose={() => setAddExerciseBlockIndex(null)}
          onAdd={(exercises) => edit.addExercisesToBlock(addExerciseBlockIndex, exercises)}
        />
      )}

      {confirm?.kind === 'block' && (
        <TrainerConfirmDialog
          open
          title="Delete this block?"
          message={
            <>
              Are you sure you want to delete &ldquo;{confirm.blockName}&rdquo;? This cannot be undone.
            </>
          }
          confirmLabel="Delete"
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            edit.deleteBlock(confirm.blockIndex);
            setConfirm(null);
          }}
        />
      )}

      {confirm?.kind === 'exercise' && (
        <TrainerConfirmDialog
          open
          title="Remove this exercise?"
          message={
            <>
              Are you sure you want to remove &ldquo;{confirm.exerciseName}&rdquo; from this block? This cannot be
              undone.
            </>
          }
          confirmLabel="Remove"
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            edit.deleteExerciseRun(confirm.blockIndex, confirm.groupKey);
            setConfirm(null);
          }}
        />
      )}

      {confirm?.kind === 'discard' && (
        <TrainerConfirmDialog
          open
          title={isCreate ? 'Leave without creating?' : 'Discard unsaved changes?'}
          message={
            isCreate
              ? 'Your workout draft will be lost.'
              : 'Your edits to this workout will be lost.'
          }
          confirmLabel="Discard"
          onCancel={() => setConfirm(null)}
          onConfirm={runDiscard}
        />
      )}
    </div>
  );
}
